import { createFileRoute, Link as RouterLink } from "@tanstack/react-router"
import {
  BookOpen,
  CreditCard,
  Database,
  FileText,
  Globe,
  Lock,
  Mail,
  Palette,
  Rss,
  Shield,
  TestTube,
  Zap,
} from "lucide-react"
import { RoughNotation } from "react-rough-notation"

import { getTurnstilePublicKey } from "@/cloudflare/turnstile"
import { NewsletterSignup } from "@/newsletter/components/newsletter-signup"
import { Badge } from "@/ui/badge"
import { Button } from "@/ui/button"
import { Card, CardContent, CardFooter } from "@/ui/card"
import { Link } from "@/ui/link"
import { P } from "@/ui/typography"

import profilePicture from "../assets/profile-picture.jpeg"
import { CourseSection } from "../components/course-section"
import { DemoShowcase } from "../components/demo-showcase"
import { Faq } from "../components/faq"
import { Features } from "../components/features"
import { Hero } from "../components/hero"
import { AnimatedCyberpunkGrid } from "../components/hero/animated-backgrounds"
import { PricingComparison } from "../components/pricing-comparison"
import { Section } from "../components/section"
import { GithubIcon, TwitterIcon } from "../components/social"
import { TechStackLogos } from "../components/tech-stack-logos"

export const Route = createFileRoute("/_brand-layout/")({
  loader: () => getTurnstilePublicKey(),
  component: Index,
})

function Index() {
  const { cloudflareTurnstilePublicKey } = Route.useLoaderData()
  return (
    <>
      <Hero
        badge={
          <Badge
            variant="outline"
            className="border-green-500/30 bg-green-500/10 px-3 py-2 text-green-700 dark:text-green-400"
            asChild
          >
            <RouterLink to="/blog/$slug" params={{ slug: "tanstack-start-cloudflare-full-stack" }}>
              v2.0 is here - check what's new →
            </RouterLink>
          </Badge>
        }
        title="Build your startup on solid foundations"
        description={
          <>
            Open source,{" "}
            <RoughNotation
              type="underline"
              color="#F38020"
              show
              strokeWidth={2}
              iterations={2}
              animationDuration={1200}
            >
              Cloudflare-native
            </RoughNotation>{" "}
            web app starter for the AI era
          </>
        }
        cta={
          <div className="flex flex-col items-center gap-8">
            <div className="flex gap-4">
              <Button asChild size="lg">
                <a href="https://docs.pagezero.dev/getting-started/quick-start/">Get started</a>
              </Button>
              <Button asChild variant="outline" size="lg">
                <a
                  className="inline-flex items-center gap-2"
                  href="https://github.com/pagezero-dev/pagezero"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <GithubIcon className="size-5 shrink-0" />
                  Star on GitHub
                </a>
              </Button>
            </div>
          </div>
        }
      >
        <AnimatedCyberpunkGrid />
      </Hero>

      <Section
        id="features"
        title="Get the basics done right. Avoid AI slop."
        description="Auth, payments, permissions, email, database, CI/CD - wired together and tested so you can write product code on day one."
      >
        <Features>
          {/* Row 1: Core product foundation */}
          <Features.Item
            title="Develop with AI Agents"
            variant="ai"
            icon={<Zap />}
            cta={
              <a
                href="https://docs.pagezero.dev/features/ai-alignment/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more →
              </a>
            }
          >
            Consistent patterns and bundled agent skills that automate common tasks. Includes an
            AGENTS.md agents actually read.
          </Features.Item>

          <Features.Item
            title="Authenticate Users"
            variant="auth"
            icon={<Lock />}
            cta={
              <a
                href="https://docs.pagezero.dev/features/authentication/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more →
              </a>
            }
          >
            OTP-based auth via email. No passwords, no third-party services. Session management and
            bot protection with Cloudflare Turnstile.
          </Features.Item>

          <Features.Item
            title="Accept Payments"
            variant="payments"
            icon={<CreditCard />}
            cta={
              <a
                href="https://docs.pagezero.dev/features/payments/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more →
              </a>
            }
          >
            Sell products and subscriptions with Polar.sh. Automatic role assignment, webhook
            handling, and global tax compliance included.
          </Features.Item>

          {/* Row 2: Infrastructure */}
          <Features.Item
            title="Manage Permissions"
            variant="permissions"
            icon={<Shield />}
            cta={
              <a
                href="https://docs.pagezero.dev/features/permissions/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more →
              </a>
            }
          >
            Simple RBAC with database roles and config-based permissions. Access granted or revoked
            automatically on payment events.
          </Features.Item>

          <Features.Item
            title="Store & Query Data"
            variant="database"
            icon={<Database />}
            cta={
              <a
                href="https://docs.pagezero.dev/features/database/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more →
              </a>
            }
          >
            Drizzle ORM with Cloudflare D1. Type-safe queries, automatic migrations, and separate
            databases for production and preview.
          </Features.Item>

          <Features.Item
            title="Deploy Globally"
            variant="deploy"
            icon={<Globe />}
            cta={
              <a
                href="https://docs.pagezero.dev/features/cloudflare/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more →
              </a>
            }
          >
            Cloudflare deploys to 300+ cities with zero cold starts. Global CDN, generous free tier,
            and pay-as-you-scale pricing.
          </Features.Item>

          {/* Row 3: Frontend & delivery */}
          <Features.Item
            title="Use pre-built UI components"
            variant="ui"
            icon={<Palette />}
            cta={
              <a
                href="https://docs.pagezero.dev/components/essentials/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more →
              </a>
            }
          >
            25+ components built with Radix UI and TailwindCSS. Accessible, customizable,
            Storybook-documented, dark mode included.
          </Features.Item>

          <Features.Item
            title="Send Emails"
            variant="email"
            icon={<Mail />}
            cta={
              <a
                href="https://docs.pagezero.dev/features/email/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more →
              </a>
            }
          >
            Resend integration with React Email and TailwindCSS templates. Pre-built templates for
            auth and access management included.
          </Features.Item>

          <Features.Item
            title="Test & Deploy"
            variant="testing"
            icon={<TestTube />}
            cta={
              <a
                href="https://docs.pagezero.dev/features/ci-cd/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more →
              </a>
            }
          >
            Vitest, Playwright, and Storybook pre-configured. Push to deploy with automatic testing
            via GitHub Actions CI/CD.
          </Features.Item>

          {/* Row 4: Content & audience */}
          <Features.Item
            title="Publish a Blog"
            variant="blog"
            icon={<BookOpen />}
            cta={
              <a
                href="https://docs.pagezero.dev/features/blog/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more →
              </a>
            }
          >
            MDX-powered blog with file-based routing, syntax highlighting, and frontmatter metadata.
            Post listing page included.
          </Features.Item>

          <Features.Item
            title="Manage Static Content"
            variant="content"
            icon={<FileText />}
            cta={
              <a
                href="https://docs.pagezero.dev/features/static-content/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more →
              </a>
            }
          >
            Shared MDX package for authoring docs, landing pages, and legal pages in Markdown with
            full React component support.
          </Features.Item>

          <Features.Item
            title="Grow a Newsletter"
            variant="newsletter"
            icon={<Rss />}
            cta={
              <a
                href="https://docs.pagezero.dev/features/newsletter/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more →
              </a>
            }
          >
            Subscriber capture with double opt-in via Resend. Broadcast integration and a signup
            widget ready to drop into any page.
          </Features.Item>
        </Features>
      </Section>

      <Section id="demo">
        <DemoShowcase />
      </Section>

      <Section
        id="tech-stack"
        title="A modern stack, deliberately chosen"
        description="A production-ready stack wired together so you don't have to - every dependency is chosen for performance, DX, and long-term stability."
        background="light"
      >
        <TechStackLogos />
      </Section>

      <Section
        id="cloudflare"
        title="Run your app for pennies"
        description="PageZERO runs on Cloudflare, so your infrastructure starts free, scales from $5/month at a fraction of the cost, with no egress fees."
        badge={
          <Badge variant="outline" className="text-muted-foreground px-3 py-2">
            <span className="mr-1 size-3 rounded-full bg-amber-600"></span>
            Cloudflare-native
          </Badge>
        }
      >
        <PricingComparison />
      </Section>

      <Section id="about" background="light" title="Hey, I'm Paweł 👋">
        <Card className="mx-auto max-w-4xl py-10 md:px-5">
          <CardContent className="text-muted-foreground">
            {/* Profile Picture Placeholder */}
            <div className="bg-muted mx-auto mb-3 size-44 flex-none rounded p-3 md:float-left md:mr-5">
              <img
                src={profilePicture}
                alt="Paweł's profile"
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
              />
            </div>
            <P>
              I've been a software engineer for 18+ years, currently working as a Senior Software
              Engineer at{" "}
              <Link href="https://www.canva.com" target="_blank" rel="noreferrer">
                Canva
              </Link>
              . Every time I started a side project, I'd burn weeks wiring up auth, payments,
              emails, permissions, and deployment before I could write a single line of actual
              product code. So I built PageZERO, a foundation I'm proud of, engineered to last so I
              can get to testing ideas fast without cutting corners.
            </P>
            <P>
              I chose to make PageZERO fully open source because I believe transparency is the
              strongest foundation you can build on. When every layer of your stack is open and
              community-driven, you get stability, security, and longevity that no proprietary
              shortcut can match.
            </P>
            <P>
              That foundation matters even more in the age of AI. AI has made coding faster than
              ever, but it still struggles with architectural decisions and can quietly introduce
              security vulnerabilities. A well-structured, open codebase gives you the confidence to
              move fast with AI without sacrificing code quality or security.
            </P>

            {/* YouTube Video Placeholder */}
            {/* <div className="mt-8 flex aspect-video w-full items-center justify-center rounded-lg bg-muted">
              YouTube Video Embed
            </div> */}
          </CardContent>
          <CardFooter className="justify-center">
            <Button variant="outline" size="lg" asChild>
              <a
                href="https://x.com/pawelgalazka"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2"
              >
                <TwitterIcon />
                Follow me
              </a>
            </Button>
          </CardFooter>
        </Card>
      </Section>

      <Section
        id="learn"
        title="From zero to a profitable web product"
        description="A video series that takes you from first commit to paying customers using AI agents, Cloudflare, and PageZERO."
        background="medium"
        badge={
          <Badge variant="outline" className="text-muted-foreground px-3 py-2">
            <span
              className="mr-2 inline-block size-3 animate-pulse rounded-full bg-amber-500"
              aria-hidden
            />
            Coming soon
          </Badge>
        }
      >
        <CourseSection />
      </Section>

      <Section id="faq" title="Frequently asked questions">
        <Faq
          items={[
            {
              question: "Is PageZERO free?",
              answer:
                "Yes. PageZERO is fully open source under the MIT license. You can use it for personal projects, client work, or commercial products without any restrictions or fees.",
            },
            {
              question: "Is PageZERO a framework or a boilerplate?",
              answer:
                "It's a boilerplate - but one that goes well beyond what that word usually implies. You get a full production-ready codebase with auth, payments, email, database, CI/CD, and more already wired together. Run the init command, own every line, modify or remove anything. No runtime dependency on PageZERO, no upgrade treadmill.",
            },
            {
              question: "What do I need to get started?",
              answer:
                "Bun (package manager) and a free Cloudflare account. Run 'bunx pagezero@latest init', follow the setup prompts, and you'll have a fully working app on localhost in seconds.",
            },
            {
              question: "What does 'for the AI era' mean?",
              answer:
                "The codebase has predictable module structure, consistent patterns, and strict TypeScript. AI agents read the AGENTS.md file and immediately understand the architecture, conventions, and how to add features correctly. Included agent skills let you scaffold components, run checks, create PRs, and perform other tasks with a single prompt.",
            },
            {
              question: "Do I need to use all the included features?",
              answer:
                "No. Features like auth, payments, and email live in their own modules under the apps/ directory. You can remove any module you don't need or add new ones following the same pattern. The codebase is designed to be a starting point, not a rigid framework.",
            },
            {
              question: "How does authentication work?",
              answer:
                "Authentication is powered by Better Auth with passwordless email OTP. No passwords to store. Users enter their email, receive a 6-digit code, and are signed in. Sessions use httpOnly secure cookies. Bot protection via Cloudflare Turnstile is included and optional to enable.",
            },
            {
              question: "How do payments work with Polar.sh?",
              answer:
                "Polar.sh handles one-time payments and subscriptions, including checkout flows, webhook handling, invoicing, and global tax compliance. When a customer completes payment, their role is automatically granted via the permissions system. On refund or subscription cancellation, the role is automatically revoked.",
            },
            {
              question: "Can I customize the UI components?",
              answer:
                "Absolutely. All 25+ components live in your project and are built with Radix UI and TailwindCSS. You can edit styles, swap variants, or replace them entirely. Each component comes with Storybook stories for isolated development and visual testing. Dark mode is included out of the box.",
            },
            {
              question: "Can I use a different database?",
              answer:
                "While optimized for Cloudflare D1 (which gives you separate databases for production and preview environments, plus visual browsing with Drizzle Studio), Drizzle ORM supports PostgreSQL, MySQL, and SQLite. You can swap adapters by updating the Drizzle configuration.",
            },
            {
              question: "How much does it cost to host?",
              answer:
                "Cloudflare Workers has a generous free tier that covers most side projects. When you outgrow it, the paid plan starts at $5/month with unlimited bandwidth and zero egress fees - a fraction of what traditional hosting costs at scale.",
            },
          ]}
        />
      </Section>

      <Section
        id="newsletter"
        title="Stay in the loop"
        description="Practical tips on building with AI agents, Cloudflare, and PageZERO. Updates on new PageZERO features. Plus 20% off the video series at launch."
        background="light"
      >
        <div className="mx-auto max-w-md">
          <NewsletterSignup cloudflareTurnstilePublicKey={cloudflareTurnstilePublicKey} />
        </div>
      </Section>
    </>
  )
}
